import React, { useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import currency from "currency.js";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import { getCustomerName, isMultiModalPaymentAvailable } from "../../common/util";
import LineItemV2 from "../../common/component/lineitem/LineItemV2";
import ButtonWithConfirmation from "../../common/ButtonWithConfirmation";
import CollectBalancePayment from "../../order/component/CollectBalancePayment";

export default function ActiveOrderRow(props) {
  const { order: initialOrder } = props;
  const { user } = useContext(UserContext);

  const [order, setOrder] = useState(initialOrder);
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder]);

  const reloadOrder = () =>
    bkstApi(`/my/order/${order.id}`).then((res) => {
      setOrder(res.data);
    });

  const printPackingSlip = () => {
    bkstApi
      .post(`/my/printer/${user.config.receiptPrinterId}/packing-slip?orderId=${order.id}`)
      .then(() => {});
  };

  const markOrderFulfilled = () => {
    bkstApi.post(`/my/order/${order.id}/fulfilled`, {}).then(() => reloadOrder());
  };

  const showCollectBalancePayment = isMultiModalPaymentAvailable(user, order) || paymentOpen;

  if (order.status === "fulfilled" || order.status === "completed") {
    return null;
  }

  return (
    <Box my={2}>
      <Accordion elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
          <Grid container alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                {getCustomerName(order.customer)}{" "}
              </Typography>
              <Typography variant="overline">
                {order.items.length} item(s) •{" "}
                {order.customer.phone ? order.customer.phone.slice(-4) : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="overline">
                  {order.fulfillment.type} • {order.fulfillment.time}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline">{order.fulfillment.location?.street}</Typography>
              </Box>
              {order.fulfillment.type === "delivery" && (
                <Box>
                  <Typography variant="overline">
                    {order.fulfillment.provider ? order.fulfillment.provider.name : "In-House"}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {+order.payment.due > 0 ? (
                <Chip label={`${currency(order.payment.due).format()} DUE`} color="error" />
              ) : (
                <Chip label="PAID IN-FULL" color="success" />
              )}
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Box pt={2} sx={{ borderTop: "1px solid #DDD" }}>
            {order.items.map((i, idx) => (
              <Box p={1} my={1} key={idx}>
                <LineItemV2 item={i} view="min" />
              </Box>
            ))}
            <Box
              pt={2}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "20px",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                component={RouterLink}
                to={`/order/${order.id}`}
              >
                View Order
              </Button>
              {user.config.receiptPrinterId && (
                <Button variant="contained" color="secondary" fullWidth onClick={printPackingSlip}>
                  Print Packing Slip
                </Button>
              )}
              {showCollectBalancePayment ? (
                <CollectBalancePayment
                  orderId={order.id}
                  onPaymentSuccess={reloadOrder}
                  onOpenChange={setPaymentOpen}
                />
              ) : (
                <ButtonWithConfirmation
                  copy="By marking this order complete, I confirm that the order was either picked up or delivered to the customer."
                  onConfirm={markOrderFulfilled}
                  fullWidth
                >
                  <Button variant="contained" color="primary" fullWidth>
                    Mark Fulfilled
                  </Button>
                </ButtonWithConfirmation>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
